import React from "react";
import get from "lodash/get";

import "./LedgerRewards.css";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import {
  getRewardRequestStatus,
  getShopifyActiveDiscounts,
  getShopifyActiveGiftCards,
} from "../../reducers/methods";
import { RequestStatus } from "../../types/requestStatus";
import { ShopifyDiscountRecord } from "../../reducers/reward/shopifyActiveDiscounts";
import { CurrencySymbol, currencySymbols } from "../../utils/currencies";
import { formatUserFacingNumber } from "../../utils/number";
import { copyText } from "../actions/copyText";

export type LedgerRewardType = "shopifyDiscount" | "shopifyGiftCard";

export interface LedgerRewardsProps extends ComponentBaseProps {
  action: string;
  borderStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  codeCellStyle?: React.CSSProperties;
  copyCellStyle?: React.CSSProperties;
  copyStyle?: React.CSSProperties;
  center: string;
  classNames: string;
  label: string;
  notAuthenticatedStyle?: React.CSSProperties;
  notAuthenticatedText?: string;
  noRewardsText?: string;
  noRewardsStyle?: React.CSSProperties;
  onClick: () => void;
  requestStatus: ReturnType<typeof getRewardRequestStatus>;
  rewardTypes: LedgerRewardType[];
  rowStyle?: React.CSSProperties;
  shopifyActiveDiscounts: ReturnType<typeof getShopifyActiveDiscounts>;
  shopifyActiveGiftCards: ReturnType<typeof getShopifyActiveGiftCards>;
  style: { [styleProp: string]: string };
  tableStyle?: React.CSSProperties;
  testMode?: string;
  valueCellStyle?: React.CSSProperties;
}

// tslint:disable-next-line:no-any
export class LedgerRewards extends ComponentBase<LedgerRewardsProps> {
  getData = () => {
    const {
      isEditing,
      rewardTypes,
      shopifyActiveDiscounts,
      shopifyActiveGiftCards,
    } = this.props;

    if (isEditing) {
      return isEditingData;
    }

    let data: ShopifyDiscountRecord[] = [];

    if (
      shopifyActiveDiscounts.count > 0 &&
      rewardTypes.includes("shopifyDiscount")
    ) {
      data = [...data, ...shopifyActiveDiscounts.records];
    }

    if (
      shopifyActiveGiftCards.count > 0 &&
      rewardTypes.includes("shopifyGiftCard")
    ) {
      data = [...data, ...shopifyActiveGiftCards.records];
    }

    return data;
  };

  getIsLoading = () => {
    const { requestStatus, rewardTypes } = this.props;
    let isLoading = false;

    if (rewardTypes.includes("shopifyDiscount")) {
      const shopifyActiveDiscountsRequestStatus = get(
        requestStatus,
        "getShopifyActiveDiscounts"
      ) as RequestStatus;

      isLoading = shopifyActiveDiscountsRequestStatus === "REQUESTING";
    }

    if (rewardTypes.includes("shopifyGiftCard")) {
      const shopifyActiveGiftCardsRequestStatus = get(
        requestStatus,
        "getShopifyActiveGiftCards"
      ) as RequestStatus;

      isLoading = shopifyActiveGiftCardsRequestStatus === "REQUESTING";
    }

    return isLoading;
  };

  renderRewards = () => {
    const {
      borderStyle,
      cellStyle,
      codeCellStyle,
      copyCellStyle,
      copyStyle,
      classNames,
      label = "ledger_rewards",
      rowStyle,
      tableStyle,
      valueCellStyle,
    } = this.props;

    const rewardsData = this.getData();

    return (
      <table
        id={this.props.configName}
        className={`LedgerRewards-container  ${classNames || ""} ${
          this.state.animationClass
        }`}
        style={tableStyle}
        aria-label={label}
        ref={(component) => (this.component = component)}
      >
        <tbody>
          {rewardsData.map((discount: ShopifyDiscountRecord) => (
            <tr key={discount.id} style={{ ...borderStyle, ...rowStyle }}>
              <td style={{ ...codeCellStyle, ...cellStyle }}>
                <div>{discount.definition.code}</div>
              </td>
              <td style={{ ...valueCellStyle, ...cellStyle }}>
                <div>
                  {currencySymbols[discount.ledgerCurrency as CurrencySymbol] ||
                    ""}
                  {formatUserFacingNumber(Number(discount.ledgerAmount))}
                  {!currencySymbols[discount.ledgerCurrency as CurrencySymbol]
                    ? ` ${discount.ledgerCurrency}`
                    : ""}
                </div>
              </td>
              <td style={{ ...copyCellStyle, ...cellStyle }}>
                <CopyCell
                  code={discount.definition.code}
                  copyStyle={copyStyle}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  renderNoRewards = () => {
    return (
      <div style={this.props.noRewardsStyle}>
        {this.props.noRewardsText || ""}
      </div>
    );
  };

  renderNotAuthenticated = () => {
    return (
      <div style={this.props.notAuthenticatedStyle}>
        {this.props.notAuthenticatedText || ""}
      </div>
    );
  };

  renderIsEditing = () => {
    const { label, testMode, style, classNames } = this.props;

    return (
      <div
        style={{ ...style }}
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        aria-label={label}
        onAnimationEnd={this.resetAnimation}
        ref={(component) => (this.component = component)}
        className={`LastShopifyReward-container ${classNames}`}
      >
        {testMode === "notAuthenticated" && this.renderNotAuthenticated()}
        {testMode === "noRewards" && this.renderNoRewards()}
        {(testMode === "rewards" || !testMode) && this.renderRewards()}
      </div>
    );
  };

  render() {
    const { data, label = "ledger_rewards", isEditing, style } = this.props;

    const isCustomerAuthenticated = get(data, "customer.authenticated", false);
    const isLoading = this.getIsLoading();
    const rewardsData = this.getData();

    if (isEditing) {
      return this.renderIsEditing();
    }

    if (isLoading) {
      return null;
    }

    return (
      <div
        style={{ ...style }}
        id={this.props.configName}
        aria-label={label}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        onAnimationEnd={this.resetAnimation}
        ref={(component) => (this.component = component)}
        className={`LedgerRewards-div-container`}
      >
        {!isCustomerAuthenticated && this.renderNotAuthenticated()}
        {isCustomerAuthenticated && rewardsData.length && this.renderRewards()}
        {isCustomerAuthenticated &&
          !rewardsData.length &&
          this.renderNoRewards()}
      </div>
    );
  }
}

interface CopyCellProps {
  code: string;
  copyStyle?: React.CSSProperties;
}

class CopyCell extends React.PureComponent<CopyCellProps> {
  state = {
    text: "Copy",
  };

  onCopy = () => {
    this.setState({ text: "Copied" });
    copyText({ text: this.props.code });
    setTimeout(() => this.setState({ text: "Copy" }), 1000);
  };

  render() {
    return (
      <div
        className="Ledger-rewards-copy"
        onClick={this.onCopy}
        style={this.props.copyStyle}
      >
        {this.state.text}
      </div>
    );
  }
}

const isEditingData = [
  {
    id: "1",
    definition: {
      code: "FBY-32234-1233",
      currency: "USD",
      amount: "20",
    },
    ledgerCurrency: "USD",
    ledgerAmount: "20",
  },
  {
    id: "2",
    definition: {
      code: "FBY-86541-3515",
      currency: "USD",
      amount: "17",
    },
    ledgerCurrency: "USD",
    ledgerAmount: "17",
  },
  {
    id: "3",
    definition: {
      code: "FBY-156851-9656",
      currency: "USD",
      amount: "5",
    },
    ledgerCurrency: "USD",
    ledgerAmount: "5",
  },
] as unknown as ShopifyDiscountRecord[];
